import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import {
  createSupportRequest,
  type SupportCategory,
  type SupportSeverity,
} from '../api/support';
import { formatApiError } from '../utils/formatApiError';
import Button from './common/ActionButton';

const categoryOptions: Array<{ value: SupportCategory; label: string }> = [
  { value: 'benchmark_feedback', label: 'Benchmark feedback' },
  { value: 'free_tier', label: 'Free tier' },
  { value: 'bug', label: 'Bug' },
  { value: 'commercial_license', label: 'Commercial license' },
  { value: 'other', label: 'Other' },
];

const severityOptions: Array<{ value: SupportSeverity; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

interface SupportRequestModalProps {
  open: boolean;
  requesterEmail?: string;
  initialCategory?: SupportCategory;
  onClose: () => void;
  onRequestCreated: (referenceCode: string) => void;
}

const isEmailLike = (value: string) => /\S+@\S+\.\S+/.test(value);

export default function SupportRequestModal({
  open,
  requesterEmail,
  initialCategory = 'benchmark_feedback',
  onClose,
  onRequestCreated,
}: SupportRequestModalProps) {
  const [category, setCategory] = useState<SupportCategory>(initialCategory);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState(requesterEmail || '');
  const [severity, setSeverity] = useState<SupportSeverity>('medium');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && requesterEmail && !email) {
      setEmail(requesterEmail);
    }
  }, [email, open, requesterEmail]);

  useEffect(() => {
    if (open) {
      setCategory(initialCategory);
    }
  }, [initialCategory, open]);

  const validationError = useMemo(() => {
    if (!subject.trim()) {
      return 'Subject is required.';
    }
    if (!message.trim()) {
      return 'Message is required.';
    }
    if (!email.trim()) {
      return 'Requester email is required.';
    }
    if (!isEmailLike(email.trim())) {
      return 'Enter a valid email address.';
    }
    return '';
  }, [email, message, subject]);

  const resetForm = () => {
    setCategory(initialCategory);
    setSubject('');
    setMessage('');
    setEmail(requesterEmail || '');
    setSeverity('medium');
    setError('');
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    setError('');
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const response = await createSupportRequest({
        category,
        subject: subject.trim(),
        message: message.trim(),
        requester_email: email.trim(),
        ...(category === 'bug' ? { severity } : {}),
        metadata: {
          user_agent: window.navigator.userAgent,
          path: window.location.pathname,
        },
      });
      resetForm();
      onRequestCreated(response.reference_code);
    } catch (err) {
      setError(
        formatApiError(
          err,
          'We could not send your request right now. Please try again.'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle sx={{ fontWeight: 700 }}>Contact Support</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              select
              label="Category"
              value={category}
              onChange={(event) => {
                const nextCategory = event.target.value as SupportCategory;
                setCategory(nextCategory);
                if (nextCategory !== 'bug') {
                  setSeverity('medium');
                }
              }}
              fullWidth
              SelectProps={{
                native: true,
              }}
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>

            <TextField
              label="Subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              inputProps={{ maxLength: 200 }}
              fullWidth
              required
            />

            <TextField
              label="Message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              multiline
              minRows={5}
              fullWidth
              required
            />

            <TextField
              label="Requester email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              fullWidth
              required
            />

            {category === 'bug' && (
              <TextField
                select
                label="Severity"
                value={severity}
                onChange={(event) => setSeverity(event.target.value as SupportSeverity)}
                fullWidth
                SelectProps={{
                  native: true,
                }}
              >
                {severityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Sending...' : 'Send request'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
